'use client';

import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';

import { formatOrderDate } from '@/lib/orders/formatOrderDate';
import { getWarrantyInfo } from '@/lib/myWatches/warranty';
import type { Watch } from '@/lib/myWatches/types';
import { getProductPlaceholder } from '@/lib/product/placeholders';
import type { WatchProductSummary } from '@/lib/service/ecs/contracts';
import type { WarrantyExtensionOffer } from '@/lib/service/repairDetailsSidebarEligibility';

import { RepairDetailsWatchSidebarAccountActions } from './RepairDetailsWatchSidebarAccountActions';
import styles from './RepairDetailsWatchSidebar.module.scss';

interface RepairDetailsWatchSidebarProps {
  referenceNumber: string;
  serialNumber: string;
  productSummary: WatchProductSummary | null;
  warranty?: Watch | null;
  warrantyCode?: number | null;
  ecsAvailable?: boolean;
  warrantyExtensionOffer?: WarrantyExtensionOffer | null;
  onWatchRegistered?: () => Promise<void>;
}

export function RepairDetailsWatchSidebar({
  referenceNumber,
  serialNumber,
  productSummary,
  warranty,
  warrantyCode = null,
  ecsAvailable = false,
  warrantyExtensionOffer = null,
  onWatchRegistered,
}: Readonly<RepairDetailsWatchSidebarProps>) {
  const locale = useLocale();
  const t = useTranslations('service.repairRequest.watch.sidebar');
  const imageUrl = productSummary?.thumbnailUrl ?? productSummary?.mediaLargeUrl ?? null;
  const displayName = productSummary?.name ?? '';
  const warrantyInfo = warranty ? getWarrantyInfo(warranty) : null;
  const validUntil = warrantyInfo?.endDate ? formatOrderDate(warrantyInfo.endDate, locale) : null;
  const activatedOn = warrantyInfo?.startDate
    ? formatOrderDate(warrantyInfo.startDate, locale)
    : null;

  return (
    <div className={styles.root} data-testid="RepairDetailsWatchSidebar">
      <h2 className={styles.title}>{t('panelTitle')}</h2>

      <div className={styles.imageWrapper}>
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={displayName || t('imageAlt')}
            fill
            className={styles.image}
            sizes="(max-width: 768px) 100vw, 270px"
          />
        ) : (
          <Image
            src={getProductPlaceholder('watch')}
            alt=""
            fill
            className={styles.placeholder}
            sizes="(max-width: 768px) 100vw, 270px"
            aria-hidden="true"
          />
        )}
      </div>

      {productSummary?.name ? <p className={styles.watchName}>{productSummary.name}</p> : null}

      <div className={styles.details}>
        <div className={styles.metaGrid}>
          <div className={styles.metaBlock}>
            <span className={styles.metaLabel}>{t('referenceLabel')}</span>
            <span className={styles.metaValue}>{referenceNumber || '—'}</span>
          </div>
          <div className={styles.metaBlock}>
            <span className={styles.metaLabel}>{t('serialLabel')}</span>
            <span className={styles.metaValue}>{serialNumber || '—'}</span>
          </div>
        </div>

        {warrantyInfo ? (
          <div className={styles.warrantyBlock} data-testid="RepairDetailsWatchSidebar-warranty">
            <span className={styles.metaLabel}>{t('warrantyLabel')}</span>
            <div className={styles.warrantyRow}>
              <span
                className={`${styles.warrantyBadge} ${
                  warrantyInfo.isActive ? styles.warrantyBadgeActive : styles.warrantyBadgeInactive
                }`}
              >
                {warrantyInfo.isActive ? t('warrantyActive') : t('warrantyInactive')}
              </span>
              <div className={styles.warrantyDates}>
                {validUntil ? <p>{t('validUntil', { date: validUntil })}</p> : null}
                {activatedOn ? <p>{t('activatedOn', { date: activatedOn })}</p> : null}
              </div>
            </div>
          </div>
        ) : null}

        {onWatchRegistered ? (
          <RepairDetailsWatchSidebarAccountActions
            warrantyCode={warrantyCode}
            warranty={warranty ?? null}
            productSummary={productSummary}
            referenceNumber={referenceNumber}
            serialNumber={serialNumber}
            ecsAvailable={ecsAvailable}
            warrantyExtensionOffer={warrantyExtensionOffer}
            onWatchRegistered={onWatchRegistered}
          />
        ) : null}
      </div>
    </div>
  );
}
